/**
 * Navigation guard for the ported frontend.
 *
 * Much smaller than the guard it replaces, because most of what that guard was
 * doing is no longer the client's job:
 *
 *  - **Two-factor.** The old guard was the *only* thing enforcing MFA: it read
 *    `is2faVerified` out of Vuex-persisted sessionStorage — a flag the client
 *    set for itself and no backend ever saw — and bounced the user to
 *    `/sixDigitAuthorization`. MFA now happens inside the authorization
 *    server's authorize flow, so a browser that holds a token has already
 *    passed it. There is nothing left to check here.
 *  - **Registration / verification / password reset.** Those flows live on the
 *    authorization server now, not in the SPA. The four `/verify*` routes stay
 *    registered, and public, only because invitation e-mails already delivered
 *    point at them: they render a hand-off that explains where the flow moved
 *    and links to it. They collect no credential. When those links have aged
 *    out, the routes and their pages go.
 *
 * What remains is genuinely a client concern: send an unauthenticated visitor
 * to sign in, remember where they were going, and make sure the session is
 * loaded before a page renders.
 */

import oidc from '@/auth/oidcClient';
import { pendingSession } from '@/auth/bootstrapSession';
import { useSessionStore } from '@/stores/session';
import { usePatientContextStore } from '@/stores/patientContext';
import store from '@/store';

/** Routes that render without a session. */
export const PUBLIC_PATHS = [
  '/auth/callback',
  '/sign-in-problem',
  // Reached with no session by design — it exists to start one.
  '/login',
  '/logout',
  '/privacy-policy',
  '/terms',
  '/protection',
  '/404',
  // Landing pages for invitation e-mails sent before the flow moved to the
  // authorization server. Public because the person holding that link has no
  // session yet — which is the entire reason they were sent it.
  '/verifyAccount',
  '/verifyNurse',
  '/verifyProvider',
  '/verifyContact',
  // A notice's "Confirm receipt" link (NT-06). The recipient may have no
  // account at all; the link's token is the credential, and only the page's
  // button confirms.
  '/confirm',
];

/**
 * Map `?dest=` values from notification emails onto app routes. Kept because
 * links in already-delivered emails still point here.
 *
 * Only a path inside this app is honoured. "Starts with a slash" was main's
 * whole test, and `//evil.example`, `/\evil.example` and their
 * percent-encoded forms all pass it while naming another host (browsers read
 * a backslash as a slash, and drop tabs and newlines from a URL before they
 * parse it). Its spec pinned those as a reported open-redirect defect; here
 * they fall back to Welcome with every other unrecognised value.
 */
function resolveDestPath(dest) {
  if (!dest) return null;
  const d = String(dest);
  if (d === '/' || d === '') return '/welcome';
  if (d === 'notes') return '/notes';
  if (d === 'professional-notes') return '/professional-notes';
  return isInAppPath(d) ? d : null;
}

/** A same-document path: one leading slash, then not a second one in any spelling. */
function isInAppPath(path) {
  // Any control character or whitespace is refused outright: a browser strips
  // `/\t/evil.example` to `//evil.example` before it parses it.
  // eslint-disable-next-line no-control-regex
  if (!path.startsWith('/') || /[\u0000- \u007f]/.test(path)) return false;

  let decoded;
  try {
    decoded = decodeURIComponent(path);
  } catch (e) {
    return false; // malformed escapes are not a path anyone was sent
  }

  return ![path, decoded].some((candidate) => /^[/\\][/\\]/.test(candidate));
}

/**
 * The navigation for a resolved destination, query and fragment included.
 *
 * vue-router reads `{path}` as a path only: `{path: '/directives/91?tab=history'}`
 * resolves to `/directives/91` and the tab the e-mail pointed at is lost.
 * Main's guard handed the string itself to `next`, which the router parses.
 */
function destLocation(dest) {
  const hashAt = dest.indexOf('#');
  const hash = hashAt === -1 ? '' : dest.slice(hashAt);
  const beforeHash = hashAt === -1 ? dest : dest.slice(0, hashAt);
  const queryAt = beforeHash.indexOf('?');
  const path = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const location = { path, replace: true };

  if (queryAt !== -1) {
    const query = {};
    new URLSearchParams(beforeHash.slice(queryAt + 1)).forEach((value, key) => {
      query[key] = Object.prototype.hasOwnProperty.call(query, key) ? [].concat(query[key], value) : value;
    });
    location.query = query;
  }
  if (hash) location.hash = hash;

  return location;
}

export function isPublicPath(path) {
  return PUBLIC_PATHS.includes(path);
}

/**
 * Circuit breaker for the sign-in redirect.
 *
 * The redirect leaves the SPA entirely, so an in-memory counter would reset on
 * every bounce; the count has to survive the navigation, which is what
 * sessionStorage is for. Three attempts inside thirty seconds is well beyond
 * any legitimate flow — a real sign-in redirects once.
 */
const ATTEMPT_KEY = 'pacmny.oidc.signInAttempts';
const ATTEMPT_WINDOW_MS = 30_000;
const ATTEMPT_LIMIT = 3;

function allowAnotherSignInAttempt() {
  let record;
  try {
    record = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY) || 'null');
  } catch (e) {
    record = null;
  }

  const now = Date.now();

  if (!record || now - record.first > ATTEMPT_WINDOW_MS) {
    record = { first: now, count: 0 };
  }

  record.count += 1;
  sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(record));

  return record.count <= ATTEMPT_LIMIT;
}

/** Called once a session is established, so a later genuine bounce starts fresh. */
export function clearSignInAttempts() {
  sessionStorage.removeItem(ATTEMPT_KEY);
}

export async function oidcGuard(to, from, next) {
  if (to.meta && to.meta.title) {
    document.title = `PACMNY - ${to.meta.title}`;
  }

  // A redirect from the identity provider may still be exchanging its code.
  // Deciding "not authenticated" while that is in flight is what turned one
  // slow round-trip into a sign-in loop.
  const inFlight = pendingSession();
  if (inFlight) {
    await inFlight;
  }

  // `/login` is public so that a signed-out visitor can reach it - it means
  // "start signing in". A signed-in one is sent on to where they were going,
  // as main's guard sends a logged-in user away from /login: rendering
  // SignInRedirect would start a new sign-in, and once the identity
  // provider's own session has idled out (15 minutes, as main's did) that asks
  // for a password the person already gave. The state machine and the
  // session-timeout handler both navigate here with a session still live.
  const signedInAtLogin = to.path === '/login' && oidc.isAuthenticated();

  if (isPublicPath(to.path) && !signedInAtLogin) {
    return next();
  }

  if (!oidc.isAuthenticated()) {
    if (!allowAnotherSignInAttempt()) {
      // Sign-in has bounced repeatedly in a short window. Something upstream is
      // refusing — a rate-limited token endpoint, a clock skew, a revoked
      // client — and redirecting again would spin forever, hammering the
      // identity provider and showing the user a blank flashing page. Stop and
      // surface it instead.
      return next({ path: '/sign-in-problem', replace: true });
    }

    // Full-page redirect to the identity provider. The browser is leaving, but
    // vue-router still needs a verdict for this navigation — returning
    // `undefined` from an async guard is what it reports as "Invalid navigation
    // guard". `false` aborts the in-flight navigation, which is exactly what is
    // happening.
    await oidc.beginLogin({ returnTo: to.fullPath });
    return next(false);
  }

  const session = useSessionStore();

  // First navigation after a reload: the token is in storage but the profile
  // has not been fetched yet. Blocking here means a page never renders against
  // an empty permission set and flashes controls the user cannot use.
  if (!session.me) {
    try {
      await session.hydrate();
      clearSignInAttempts();
    } catch (e) {
      if (e.isAuthError) {
        await oidc.beginLogin({ returnTo: to.fullPath });
        return next(false);
      }
      // A non-auth failure (the API is down) should not trap the user in a
      // redirect loop; let the page render and surface its own error.
    }
  }

  // Un-onboarded nurses (Decision D3) must complete self-onboarding before
  // accessing any clinical or organizational services.
  if (session.needsOnboarding) {
    if (to.path !== '/onboarding') {
      return next({ path: '/onboarding', replace: true });
    }
    return next();
  }

  // An onboarded user navigating to /onboarding is sent to /welcome.
  if (to.path === '/onboarding') {
    return next({ path: '/welcome', replace: true });
  }

  // Fill the legacy application model before anything renders against it.
  //
  // Roughly 129 components read `login/getAllData`, which the Yii2 sign-in
  // response used to populate. Nothing populated it after the port, so
  // `allData.user` stayed undefined until some unrelated action happened to
  // dispatch this — the profile page threw on `credential.dob`.
  //
  // Keyed on the model being absent rather than on `session.me` being absent:
  // `bootstrapSession` may already have hydrated the session during the OIDC
  // callback, in which case the block above is skipped and the first render
  // would race the fetch. Failures are non-fatal — every page also reads the
  // Pinia stores, which are the real source.
  //
  // The test is `user`, not `phrData`. The module's initial state is
  // `{ patient: {} }` — deliberately, so `getAllData` falls through to the
  // patient context — which means `phrData` is never falsy and a check on it
  // would skip this fetch forever. `user` only exists once the fetch has
  // landed.
  if (!store.state.login || !store.state.login.phrData || !store.state.login.phrData.user) {
    try {
      await store.dispatch('login/updateApplicationModel');
    } catch (e) {
      /* the page renders from the Pinia stores regardless */
    }
  }

  // Choose the default chart once per session.
  //
  // `session.hydrate()` does this too, but the guard only calls hydrate when
  // `session.me` is missing — and it can already be populated by the time the
  // first navigation runs. A patient would then land on `/welcome` with no
  // chart open and every chart-keyed read would come back null. Keyed on
  // `initialised` rather than on `patient`, because "no chart" is the right
  // answer for staff and must not be retried on every navigation.
  const chartContext = usePatientContextStore();

  if (!chartContext.initialised) {
    try {
      await chartContext.initialise();
    } catch (e) {
      /* the chart picker still works; the session is valid either way */
    }
  }

  const dest = resolveDestPath(to.query && to.query.dest);
  if (dest && (to.path === '/' || to.path === '/login')) {
    return next(destLocation(dest));
  }

  if (to.path === '/' || to.path === '/login') {
    return next({ path: '/welcome', replace: true });
  }

  // Route-declared permission gate. Cosmetic: the API enforces the same
  // permission, so a user who bypasses this reaches a 403, not data.
  const required = to.meta && to.meta.permission;
  if (required && !session.can(required)) {
    return next({ path: '/welcome', replace: true });
  }

  // A chart page needs a chart.
  //
  // This is a safety gate, not a convenience. Every one of these pages reads
  // its patient from the shared context, and with nothing selected they render
  // their empty state — "0 Active", "Add First Allergy". To a clinician that
  // reads as *this patient has no allergies*, which is a different and far more
  // dangerous claim than *no patient is open*. The legacy hid the distinction
  // because the Yii2 session always held a patient; there is no server session
  // now, so the SPA has to be explicit about it.
  //
  // Patients are never bounced: their own chart is the only one they have, and
  // `patientContext.initialise()` has already selected it.
  if (to.meta && to.meta.requiresPatient) {
    if (!chartContext.patient) {
      return next({
        path: '/patients',
        replace: true,
        query: { chart: to.fullPath },
      });
    }
  }

  return next();
}

export default oidcGuard;
